import type { BillingCard } from '../models/useBilling'
import { capitalize } from './general.ts'

/**
 * How to name the payment method a customer is charged on.
 *
 * ⚠⚠ IT IS NOT ALWAYS A CARD, AND READING IT AS ONE IS router#1171. A customer who checks out
 * through Stripe Link saves a method of type `link`: it is charged every month and it carries NO
 * brand, NO last four and NO expiry, because the card sits behind the Link account. This panel
 * printed `{brand} •••• {last4}` unconditionally, so that customer would have read
 * " •••• " beside a real charge — and before the router fix they read "Add a card" instead.
 *
 * The router now sends `type` always, and `account` for a method that a brand and last four
 * cannot identify — the Link account's email, or the bank's name.
 */

/**
 * `link` and `us_bank_account` are Stripe's own spellings; this is what a person reads.
 *
 * ⚠ `type` is TYPED as required and is ABSENT in the deploy window, so this takes it as optional
 * and answers a generic name rather than throwing. Reached only when the method exposes no brand
 * and no last four, which is the one shape an older router never sent — but a `type.replace` on
 * `undefined` there would take the whole billing panel down instead of one line.
 */
const typeName = (type: string | undefined): string => {
  if (!type) return 'Payment method'
  if (type === 'link') return 'Link'
  if (type === 'us_bank_account') return 'Bank account'
  return capitalize(type.replace(/_/g, ' '))
}

/**
 * `title` names the method and `detail` is the smaller line beside it, empty when there is none.
 *
 * ⚠ A router that predates router#1171 sends neither `type` nor `account`, which is the state
 * during the deploy window — router first, then this. A card still has its brand and last four
 * there, so the first branch answers exactly as it did before and nothing renders differently.
 */
export const describePaymentMethod = (card: BillingCard): { title: string; detail: string } => {
  const expiry = card.expYear ? `expires ${String(card.expMonth).padStart(2, '0')}/${card.expYear}` : ''

  // A card, from any router version.
  if (card.brand) return { title: `${capitalize(card.brand)} •••• ${card.last4}`, detail: expiry }

  // A bank account: no brand, but it does have digits, and `account` names the bank.
  if (card.last4) return { title: `${card.account || typeName(card.type)} •••• ${card.last4}`, detail: expiry }

  // Link, and anything else with no digits to show. The account is the only identification there
  // is, and inventing a brand or a last four would be a statement about a card nobody has seen.
  // ⚠ `?? ''`, for the same deploy window: `account` is absent from an older payload, and the
  // declared return type says `detail` is a string.
  return { title: typeName(card.type), detail: card.account ?? '' }
}

/**
 * What to call the thing a charge lands on, when the charge is about to be described to the
 * customer — including when nothing is known about it (app#241).
 *
 * ⚠⚠ THE FALLBACK MUST NOT ASSERT AN INSTRUMENT, and the one it replaced did. `CreditBlock` said
 * "the card on file" whenever `card` was null, which happened on `/usage`: `loadSummary` fills that
 * field and only the billing page called it (both pages do since app#268). So the SAME purchase,
 * on the same organization, was described two ways —
 *
 *     /billing   "$600.00 is charged to Link now."
 *     /usage     "$600.00 is charged to the card on file now."
 *
 * — and the second is false, not merely vague: measured 2026-09-03, that organization's default
 * method is `type=link`, `card=null`. It has no card. The static caption under the field said "the
 * card on file" on BOTH pages, so even the correct page contradicted itself two lines apart.
 *
 * ⚠ The reasoning beside the old fallback was right and is kept: a confirmation that says
 * " •••• " about a real charge is worse than one that names no method at all. `describePaymentMethod`
 * is what avoids that, and this function only decides what to say when there is nothing to describe.
 * The gap was between that intent and the string — "the card on file" names a method rather than
 * declining to.
 *
 * ⚠ ONE VALUE FOR BOTH RENDERINGS. The dialog and the caption below the field are two sentences
 * about one fact, and they drifted because each held its own words. `CreditBlock` now reads this
 * once and prints it in both places, so a future change cannot move one and leave the other.
 */
export const chargeTargetName = (card: BillingCard | null | undefined): string =>
  card ? describePaymentMethod(card).title : 'your default payment method'

